// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToolSuggestion } from './AIModule';
import { useToggleSet } from './useToggleSet';

interface ToolSuggestionsProps {
  isVisible: boolean;
  isLoading: boolean;
  tools: ToolSuggestion[];
  onSelectTool: (tool: ToolSuggestion) => void;
  onClose?: () => void;
}

// Thin outline chevron. Points down when collapsed; rotates to point up when
// the row's description is expanded. Stroke-only (no fill) for a light look.
const ExpandChevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ToolSuggestions: React.FC<ToolSuggestionsProps> = ({
  isVisible,
  isLoading,
  tools,
  onSelectTool,
  onClose,
}) => {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const isDraggingScrollbar = React.useRef(false);
  const scrollbarInteractionTimeout = React.useRef<NodeJS.Timeout | null>(null);

  // Names of tools whose full description is expanded. Each row is toggled
  // independently via its chevron; nothing expands automatically. Reset when
  // the popup closes so it reopens fully collapsed.
  const [expanded, toggleExpand, resetExpanded] = useToggleSet();

  React.useEffect(() => {
    if (!isVisible) resetExpanded();
  }, [isVisible, resetExpanded]);

  // Names whose collapsed preview is actually clipped (the full description is
  // not visible on the single preview line). Measured from the DOM so it also
  // catches CSS ellipsis on short-but-wide text — not just the length cut. The
  // chevron is only rendered for these tools; tools whose description fully
  // fits need no expand affordance.
  const previewRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const [truncated, setTruncated] = React.useState<Set<string>>(new Set());

  const measureTruncation = React.useCallback(() => {
    setTruncated((prev) => {
      const next = new Set<string>();
      Object.entries(previewRefs.current).forEach(([name, el]) => {
        if (el && el.scrollWidth - el.clientWidth > 1) next.add(name);
      });
      const unchanged =
        next.size === prev.size && Array.from(next).every((n) => prev.has(n));
      return unchanged ? prev : next;
    });
  }, []);

  React.useLayoutEffect(() => {
    measureTruncation();
  }, [tools, isVisible, expanded, measureTruncation]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !isVisible || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measureTruncation());
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, measureTruncation]);

  React.useEffect(() => {
    if (!isVisible || !onClose) return;

    const handleDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const container = containerRef.current;

      if (!container || !target) return;

      // Check if the mousedown is within the container bounds
      const rect = container.getBoundingClientRect();
      const clickX = e.clientX;
      const clickY = e.clientY;

      const isWithinBounds =
        clickX >= rect.left &&
        clickX <= rect.right &&
        clickY >= rect.top &&
        clickY <= rect.bottom;

      if (isWithinBounds) return;
      if (isDraggingScrollbar.current) return;
      if (container.contains(target)) return;
      onClose();
    };

    const handleMouseMove = () => {
      // Reset scrollbar dragging flag on any mouse move after a delay
      if (isDraggingScrollbar.current) {
        if (scrollbarInteractionTimeout.current) {
          clearTimeout(scrollbarInteractionTimeout.current);
        }
        scrollbarInteractionTimeout.current = setTimeout(() => {
          isDraggingScrollbar.current = false;
        }, 100);
      }
    };

    const handleMouseUp = () => {
      // Reset scrollbar dragging flag after mouse up
      setTimeout(() => {
        isDraggingScrollbar.current = false;
      }, 50);
    };

    document.addEventListener('mousedown', handleDocMouseDown, true); // Use capture phase
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown, true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (scrollbarInteractionTimeout.current) {
        clearTimeout(scrollbarInteractionTimeout.current);
      }
    };
  }, [isVisible, onClose]);

  const detectScrollbarClick = (
    e: React.MouseEvent,
    element: HTMLElement,
  ): boolean => {
    const rect = element.getBoundingClientRect();
    const scrollbarWidth = element.offsetWidth - element.clientWidth;

    // If there's a visible scrollbar
    if (scrollbarWidth > 0) {
      return e.clientX >= rect.right - scrollbarWidth;
    }

    const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
    if (hasVerticalScrollbar) {
      return e.clientX >= rect.right - 15;
    }

    return false;
  };

  const handleContainerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;

    const isScrollbarClick = detectScrollbarClick(e, container);

    if (isScrollbarClick) {
      isDraggingScrollbar.current = true;
      return;
    }

    e.preventDefault();
  };

  const handleContainerMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleContainerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  if (!isVisible) return null;

  const renderSecondary = (tool: ToolSuggestion, isExpanded: boolean) => {
    const description = tool.description || t('select-tool');
    if (isExpanded) {
      return (
        <span className="mt-1 block whitespace-pre-wrap break-words leading-normal [overflow-wrap:anywhere]">
          {description}
        </span>
      );
    }
    const preview =
      tool.description && tool.description.length > 100
        ? `${tool.description.substring(0, 100)}...`
        : description;
    return (
      <span
        ref={(el) => {
          previewRefs.current[tool.name] = el;
        }}
        className="block overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {preview}
      </span>
    );
  };

  return (
    <div
      id="id-tool-suggestions-wrapper"
      data-testid="tool-suggestions-wrapper"
      className="absolute bottom-[70px] left-[10px] z-[1000] max-h-[400px] min-h-[200px] w-[calc(100%-32px)] max-w-[600px] overflow-hidden overscroll-contain rounded-xl border border-black/10 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
    >
      <div
        ref={containerRef}
        id="id-tool-suggestions-container"
        data-testid="tool-suggestions-container"
        onMouseDown={handleContainerMouseDown}
        onMouseUp={handleContainerMouseUp}
        onScroll={handleContainerScroll}
        className="max-h-[398px] overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg bg-[#f9f9f9] p-2 shadow-none [scrollbar-gutter:stable] [&::-webkit-scrollbar-thumb:hover]:bg-black/30 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-black/5 [&::-webkit-scrollbar]:w-2"
      >
        <div role="listbox" aria-label={t('select-tool')} className="text-sm">
          {isLoading ? (
            <div
              id="id-tool-suggestions-loading"
              data-testid="tool-suggestions-loading"
              className="px-4 py-2"
            >
              {t('loading-tools')}
            </div>
          ) : tools.length > 0 ? (
            tools.map((tool) => {
              const isExpanded = expanded.has(tool.name);
              // Only offer the expand affordance when the preview is clipped
              // (or already open, so it can be collapsed again).
              const showChevron = isExpanded || truncated.has(tool.name);
              return (
                <div
                  key={tool.name}
                  role="option"
                  aria-selected={isExpanded}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectTool(tool);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectTool(tool);
                    }
                  }}
                  id={`id-tool-suggestion-${tool.name}`}
                  data-testid={`tool-suggestion-${tool.name}`}
                  tabIndex={0}
                  className="group flex cursor-pointer items-start px-4 py-2 hover:bg-[#E3F2FD]"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">{tool.name}</div>
                    {renderSecondary(tool, isExpanded)}
                  </div>
                  {/* Fixed-width slot keeps every row's text column the same
                      width (so truncation detection is stable) whether or not
                      a chevron is present. */}
                  <span className="ml-2 mt-0.5 inline-flex w-6 shrink-0 items-start justify-center">
                    {showChevron && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={
                          isExpanded
                            ? t('collapse-description')
                            : t('expand-description')
                        }
                        aria-expanded={isExpanded}
                        data-testid={`tool-expand-${tool.name}`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleExpand(tool.name);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpand(tool.name);
                          }
                        }}
                        className="inline-flex cursor-pointer items-center justify-center rounded p-0.5 text-[#64748B] opacity-0 transition-opacity hover:bg-black/[0.06] group-focus-within:opacity-100 group-hover:opacity-100"
                      >
                        <ExpandChevron expanded={isExpanded} />
                      </span>
                    )}
                  </span>
                </div>
              );
            })
          ) : (
            <div
              id="id-tool-suggestions-empty"
              data-testid="tool-suggestions-empty"
              className="px-4 py-2"
            >
              {t('no-tools-available')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolSuggestions;
